/**
 * Which paths no formula scores — the warning's data.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Deliberately mirrors the engine's own rule (a group scores a path unless its
 * `ignoredPathBankIds` names it) so the banner can never claim something the
 * optimizer disagrees with. Two different problems, kept apart because the fix
 * is different: nothing targets the layer at all, versus every formula that
 * does has been told to skip this path.
 */

import type {
  FlowOptimizerItemPreview,
  FormulaConfig,
  FormulaGroup,
} from '@trafficpulse/shared';

export type UncoveredReason = 'no-formula-layer' | 'ignored-by-all';

export interface UncoveredPath {
  itemId: string;
  itemName: string;
  pathBankId?: string;
  reason: UncoveredReason;
}

/** Legacy empty/undefined `appliesTo` means every layer. */
const scoresPaths = (g: FormulaGroup): boolean =>
  !g.appliesTo?.length || g.appliesTo.includes('paths');

const skips = (g: FormulaGroup, bankId?: string): boolean =>
  Boolean(bankId) && Boolean(g.ignoredPathBankIds?.includes(bankId as string));

export function findUncoveredPaths(
  formula: FormulaConfig | undefined,
  items: ReadonlyArray<FlowOptimizerItemPreview> | undefined,
): UncoveredPath[] {
  const applicable = (formula?.groups ?? []).filter(scoresPaths);
  const out: UncoveredPath[] = [];
  for (const item of items ?? []) {
    if (item.layer !== 'paths') continue;
    const scoring = applicable.filter((g) => !skips(g, item.pathBankId));
    if (scoring.length > 0) continue;
    out.push({
      itemId: item.itemId,
      itemName: item.itemName || item.itemId,
      pathBankId: item.pathBankId,
      reason: applicable.length === 0 ? 'no-formula-layer' : 'ignored-by-all',
    });
  }
  return out;
}

/**
 * Bank id -> names of the formula groups that skip it, for the paths layer.
 *
 * Feeds the score popup: without it a skipped group simply VANISHES from a
 * path's breakdown and the survivors print re-shared weights that match no
 * config screen, leaving the user no way to tell why two sibling paths were
 * scored differently.
 */
export function skippingGroupNamesByBankId(
  formula: FormulaConfig | undefined,
): ReadonlyMap<string, string[]> {
  const out = new Map<string, string[]>();
  for (const group of (formula?.groups ?? []).filter(scoresPaths)) {
    for (const bankId of group.ignoredPathBankIds ?? []) {
      const name = group.name?.trim() || 'Untitled group';
      out.set(bankId, [...(out.get(bankId) ?? []), name]);
    }
  }
  return out;
}
